import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize } from 'rxjs';
import { DataPreview } from '../../services/data-preview/data-preview';
import { NewMap } from '../../services/new-mapping/new-map';
import { InfoStateService } from '../../services/info-state/info-state';
import { BugReportService } from '../../services/bug-report/bug-report';

type HelpSection = 'menu' | 'mappingInitial' | 'mappingRetrain';

type HelpImage = {
  id: string;
  title: string;
  caption: string;
  src: string;
};

@Component({
  selector: 'app-help-widget',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './help-widget.html',
  styleUrl: './help-widget.scss'
})
export class HelpWidget {
  private readonly router = inject(Router);
  private readonly dataPreviewService = inject(DataPreview);
  private readonly mapperService = inject(NewMap);
  private readonly infoStateService = inject(InfoStateService);
  private readonly bugReportService = inject(BugReportService);

  readonly isOpen = signal<boolean>(false);
  readonly currentSection = signal<HelpSection>('menu');
  readonly hasSectionOverride = signal<boolean>(false);
  readonly isOpeningBugReport = signal<boolean>(false);
  readonly bugReportError = signal<string | null>(null);
  readonly activeImage = signal<HelpImage | null>(null);

  readonly currentRoute = signal<string>(this.router.url);
  readonly previousRoute = signal<string>('');

  readonly initialMappingImages: HelpImage[] = [
    {
      id: 'init-info',
      title: 'Infographic',
      caption: 'Initial Mapping - Infographic',
      src: '/assets/images/Help/Init_Mapping_1.png'
    },
    {
      id: 'init-flow',
      title: 'Flow',
      caption: 'Initial Mapping - Flow',
      src: '/assets/images/Help/Init_Mapping_2.png'
    }
  ];

  readonly retrainMappingImages: HelpImage[] = [
    {
      id: 'retrain-info',
      title: 'Infographic',
      caption: 'Retrain Mapping - Infographic',
      src: '/assets/images/Help/Retrain_Mapping_1.png'
    },
    {
      id: 'retrain-flow',
      title: 'Flow',
      caption: 'Retrain Mapping - Flow',
      src: '/assets/images/Help/Retrain_Mapping_2.png'
    }
  ];

  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const nav = event as NavigationEnd;
        this.previousRoute.set(this.currentRoute());
        this.currentRoute.set(nav.urlAfterRedirects);

        if (!this.hasSectionOverride() && !this.isOpen()) {
          this.currentSection.set(this.resolveDefaultSection());
        }
      });
  }

  openPanel(): void {
    if (!this.hasSectionOverride()) {
      this.currentSection.set(this.resolveDefaultSection());
    }
    this.isOpen.set(true);
  }

  closePanel(): void {
    this.isOpen.set(false);
  }

  goToMenu(): void {
    this.currentSection.set('menu');
    this.hasSectionOverride.set(true);
  }

  openInitialMapping(): void {
    this.currentSection.set('mappingInitial');
    this.hasSectionOverride.set(true);
  }

  openRetrainMapping(): void {
    this.currentSection.set('mappingRetrain');
    this.hasSectionOverride.set(true);
  }

  showImage(image: HelpImage): void {
    this.activeImage.set(image);
  }

  closeImage(): void {
    this.activeImage.set(null);
  }

  reportBug(): void {
    if (this.isOpeningBugReport()) {
      return;
    }

    const coId = this.resolveCoId();
    this.bugReportError.set(null);
    this.isOpeningBugReport.set(true);

    const request$ = coId
      ? this.bugReportService.getPrefilledLink(coId)
      : this.bugReportService.getGeneralLink();

    request$
      .pipe(finalize(() => this.isOpeningBugReport.set(false)))
      .subscribe({
        next: (formUrl) => {
          if (typeof window === 'undefined') {
            return;
          }

          const popup = window.open(formUrl, '_blank', 'noopener,noreferrer');
          if (!popup) {
            this.bugReportError.set('Popup blocked. Please allow popups and try again.');
          }
        },
        error: () => {
          this.bugReportError.set('Unable to open bug report form right now. Please try again.');
        }
      });
  }

  get panelTitle(): string {
    const section = this.currentSection();
    if (section === 'mappingInitial') {
      return 'Mapping (Initial)';
    }
    if (section === 'mappingRetrain') {
      return 'Mapping (Retrain)';
    }
    return 'Need Help?';
  }

  get panelSubtitle(): string {
    const section = this.currentSection();
    if (section === 'mappingInitial') {
      return 'How initial mapping works';
    }
    if (section === 'mappingRetrain') {
      return 'How retrain mapping works';
    }
    return 'FAQ and support';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.activeImage()) {
      this.closeImage();
      return;
    }

    if (this.isOpen()) {
      this.closePanel();
    }
  }

  private resolveDefaultSection(): HelpSection {
    const route = this.currentRoute();
    const prevRoute = this.previousRoute();
    const isRetrainFlow = this.infoStateService.isRetrainMode();

    if (isRetrainFlow) {
      return 'mappingRetrain';
    }

    if (route.includes('/process')) {
      return 'mappingInitial';
    }

    if (prevRoute.includes('/preview') && route.includes('/process')) {
      return 'mappingInitial';
    }

    return 'menu';
  }

  private resolveCoId(): string | null {
    const fromPreProcess = this.dataPreviewService.preProcessExcelData$()()?.coId;
    if (fromPreProcess) {
      return fromPreProcess;
    }

    const fromMappingPayload = this.dataPreviewService.mappingPayload$()?.coId;
    if (fromMappingPayload) {
      return fromMappingPayload;
    }

    const fromBedrock = this.mapperService.getBedrockDetails()?.coId;
    if (fromBedrock) {
      return fromBedrock;
    }

    const fromRoute = this.extractCoIdFromUrl(this.currentRoute())
      ?? this.extractCoIdFromUrl(this.previousRoute());
    if (fromRoute) {
      return fromRoute;
    }

    return null;
  }

  private extractCoIdFromUrl(url: string): string | null {
    const match = url.match(/\/history\/([^/?#]+)/);
    return match?.[1] ?? null;
  }
}
